import { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  approveOpsJob,
  listExtrasMonth,
  listOpsUsers,
  type ExtrasMonthResponse,
  type PlanningUserRecord,
} from '@/features/planning/api';
import type { Language } from '@/types';

interface ExtrasWorkspaceProps {
  language: Language;
}

const copy = {
  en: {
    title: 'Extra cleanings',
    description: 'Monthly overview per BRF with approval workflow.',
    prev: 'Previous',
    next: 'Next',
    refresh: 'Refresh',
    filterCleaner: 'Cleaner',
    filterStatus: 'Status',
    filterSite: 'BRF',
    filterType: 'Type',
    all: 'All',
    unassigned: 'Unassigned',
    typeOneOff: 'One-off',
    typeRecurring: 'Recurring',
    statusPlanned: 'Planned',
    statusAssigned: 'Assigned',
    statusCompleted: 'Completed',
    statusApproved: 'Approved',
    statusCancelled: 'Cancelled',
    kpiTotal: 'Total extras',
    kpiPending: 'Awaiting approval',
    kpiApproved: 'Approved',
    kpiInvoice: 'To invoice',
    tblDate: 'Date',
    tblLabel: 'Extra',
    tblCleaner: 'Cleaner',
    tblPoints: 'Pts',
    tblPrice: 'Price',
    tblStatus: 'Status',
    approve: 'Approve',
    approving: 'Approving…',
    noExtras: 'No extras in this month.',
  },
  sv: {
    title: 'Extra städningar',
    description: 'Månadsöversikt per BRF med godkännandeflöde.',
    prev: 'Föregående',
    next: 'Nästa',
    refresh: 'Uppdatera',
    filterCleaner: 'Städare',
    filterStatus: 'Status',
    filterSite: 'BRF',
    filterType: 'Typ',
    all: 'Alla',
    unassigned: 'Ej tilldelad',
    typeOneOff: 'Engång',
    typeRecurring: 'Återkommande',
    statusPlanned: 'Planerad',
    statusAssigned: 'Tilldelad',
    statusCompleted: 'Klar',
    statusApproved: 'Godkänd',
    statusCancelled: 'Avbruten',
    kpiTotal: 'Totalt extra',
    kpiPending: 'Väntar godkännande',
    kpiApproved: 'Godkända',
    kpiInvoice: 'Faktureras',
    tblDate: 'Datum',
    tblLabel: 'Extra',
    tblCleaner: 'Städare',
    tblPoints: 'Poäng',
    tblPrice: 'Pris',
    tblStatus: 'Status',
    approve: 'Godkänn',
    approving: 'Godkänner…',
    noExtras: 'Inga extra denna månad.',
  },
} as const;

const MONTH_NAMES_SV = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function currentMonthParam(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function shiftMonth(month: string, delta: number): string {
  const [yStr, mStr] = month.split('-');
  const d = new Date(Number.parseInt(yStr, 10), Number.parseInt(mStr, 10) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function parseHashFilters(): Record<string, string> {
  const hash = window.location.hash;
  const q = hash.split('?')[1];
  if (!q) return {};
  const params = new URLSearchParams(q);
  return Object.fromEntries(params.entries());
}

function updateHashFilters(next: Record<string, string>): void {
  const hashBase = window.location.hash.split('?')[0] || '#/extras';
  const q = Object.entries(next)
    .filter(([, v]) => v && v !== 'all')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const nextHash = q ? `${hashBase}?${q}` : hashBase;
  if (window.location.hash !== nextHash) {
    window.location.hash = nextHash;
  }
}

function formatSek(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(value);
}

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'info' | 'destructive' {
  switch (status) {
    case 'approved': return 'success';
    case 'completed': return 'warning';
    case 'assigned': return 'info';
    case 'cancelled': return 'destructive';
    default: return 'default';
  }
}

export function ExtrasWorkspace({ language }: ExtrasWorkspaceProps) {
  const { session, profile } = useAuth();
  const text = copy[language];
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';

  const initial = parseHashFilters();
  const [month, setMonth] = useState<string>(initial.month ?? currentMonthParam());
  const [cleanerId, setCleanerId] = useState<string>(initial.cleanerId ?? 'all');
  const [status, setStatus] = useState<string>(initial.status ?? 'all');
  const [siteId, setSiteId] = useState<string>(initial.siteId ?? 'all');
  const [cadenceType, setCadenceType] = useState<string>(initial.cadenceType ?? 'all');

  const [data, setData] = useState<ExtrasMonthResponse | null>(null);
  const [cleaners, setCleaners] = useState<PlanningUserRecord[]>([]);
  const [allSites, setAllSites] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const [collapsedOverride, setCollapsedOverride] = useState<Record<string, boolean>>({});
  const [cleanerOpen, setCleanerOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);

  const [yearStr, monthStr] = month.split('-');
  const monthNumber = Number.parseInt(monthStr, 10);
  const yearNumber = Number.parseInt(yearStr, 10);
  const monthName = (language === 'sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN)[monthNumber - 1];

  useEffect(() => {
    updateHashFilters({ month, cleanerId, status, siteId, cadenceType });
  }, [month, cleanerId, status, siteId, cadenceType]);

  useEffect(() => {
    if (!token) return;
    listOpsUsers(token)
      .then((users) => setCleaners(users.filter((u) => u.active)))
      .catch(() => setCleaners([]));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/ops/rules', {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : { sites: [] }))
      .then((res: { sites?: Array<{ id: string; name: string }> }) => {
        const list = Array.isArray(res.sites) ? res.sites : [];
        setAllSites(
          list
            .map((s) => ({ id: s.id, name: s.name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        );
      })
      .catch(() => setAllSites([]));
  }, [token]);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listExtrasMonth(
        {
          month,
          cleanerId: cleanerId === 'all' ? null : cleanerId,
          siteId: siteId === 'all' ? null : siteId,
          status: status === 'all' ? null : status,
          cadenceType: cadenceType === 'all' ? null : (cadenceType as 'ONE_OFF' | 'EXACT_MONTHS'),
        },
        token,
      );
      setData(res);
      setCollapsedOverride({});
    } catch (err) {
      console.error('Failed to load extras', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, month, cleanerId, status, siteId, cadenceType]);

  const siteOptions = useMemo(() => {
    if (allSites.length > 0) return allSites;
    if (!data) return [];
    return data.sites.map((s) => ({ id: s.site_id, name: s.site_name }));
  }, [allSites, data]);

  const cleanerLabel = useMemo(() => {
    if (cleanerId === 'all') return text.all;
    if (cleanerId === 'unassigned') return text.unassigned;
    const c = cleaners.find((x) => x.id === cleanerId);
    return c?.full_name ?? c?.email ?? text.filterCleaner;
  }, [cleanerId, cleaners, text]);

  const siteLabel = useMemo(() => {
    if (siteId === 'all') return text.all;
    const s = siteOptions.find((x) => x.id === siteId);
    return s?.name ?? text.filterSite;
  }, [siteId, siteOptions, text]);

  const handleApprove = async (jobId: string) => {
    if (!token) return;
    setApproving(jobId);
    try {
      await approveOpsJob({ jobId }, token);
      await loadData();
    } catch (err) {
      console.error('Approve failed', err);
    } finally {
      setApproving(null);
    }
  };

  const siteHasPendingApproval = (site: ExtrasMonthResponse['sites'][number]): boolean =>
    site.extras.some((e) => e.status === 'completed');

  const isSiteCollapsed = (site: ExtrasMonthResponse['sites'][number]): boolean => {
    const override = collapsedOverride[site.site_id];
    if (override !== undefined) return override;
    return !siteHasPendingApproval(site);
  };

  const toggleCollapse = (site: ExtrasMonthResponse['sites'][number]) => {
    const current = isSiteCollapsed(site);
    setCollapsedOverride((prev) => ({ ...prev, [site.site_id]: !current }));
  };

  const kpiInvoice = data?.grandTotals.price_ex_moms ?? 0;
  const kpiTotal = data?.grandTotals.count ?? 0;
  const kpiPending = (data?.grandTotals.byStatus.completed ?? 0) + (data?.grandTotals.byStatus.assigned ?? 0);
  const kpiApproved = data?.grandTotals.byStatus.approved ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{text.title}</h1>
          <p className="text-sm text-muted-foreground">{text.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} aria-label={text.prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[160px] text-center font-semibold">
            {monthName} {yearNumber}
          </div>
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} aria-label={text.next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1">{text.refresh}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{text.kpiTotal}</div>
            <div className="text-2xl font-bold">{kpiTotal}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{text.kpiPending}</div>
            <div className="text-2xl font-bold">{kpiPending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{text.kpiApproved}</div>
            <div className="text-2xl font-bold">{kpiApproved}</div>
          </CardContent>
        </Card>
        {isAdmin && (
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{text.kpiInvoice}</div>
              <div className="text-2xl font-bold">{formatSek(kpiInvoice)}</div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <label className="text-xs text-muted-foreground">{text.filterCleaner}</label>
          <Popover open={cleanerOpen} onOpenChange={setCleanerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={cleanerOpen} className="w-full justify-between">
                <span className="truncate">{cleanerLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder={text.filterCleaner} />
                <CommandList>
                  <CommandEmpty>—</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setCleanerId('all'); setCleanerOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', cleanerId === 'all' ? 'opacity-100' : 'opacity-0')} />
                      {text.all}
                    </CommandItem>
                    <CommandItem value="unassigned" onSelect={() => { setCleanerId('unassigned'); setCleanerOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', cleanerId === 'unassigned' ? 'opacity-100' : 'opacity-0')} />
                      {text.unassigned}
                    </CommandItem>
                    {cleaners.map((c) => {
                      const label = c.full_name ?? c.email;
                      return (
                        <CommandItem key={c.id} value={label} onSelect={() => { setCleanerId(c.id); setCleanerOpen(false); }}>
                          <Check className={cn('mr-2 h-4 w-4', cleanerId === c.id ? 'opacity-100' : 'opacity-0')} />
                          {label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs text-muted-foreground">{text.filterStatus}</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{text.all}</SelectItem>
              <SelectItem value="planned">{text.statusPlanned}</SelectItem>
              <SelectItem value="assigned">{text.statusAssigned}</SelectItem>
              <SelectItem value="completed">{text.statusCompleted}</SelectItem>
              <SelectItem value="approved">{text.statusApproved}</SelectItem>
              <SelectItem value="cancelled">{text.statusCancelled}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[240px]">
          <label className="text-xs text-muted-foreground">{text.filterSite}</label>
          <Popover open={siteOpen} onOpenChange={setSiteOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={siteOpen} className="w-full justify-between">
                <span className="truncate">{siteLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput placeholder={text.filterSite} />
                <CommandList>
                  <CommandEmpty>—</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setSiteId('all'); setSiteOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', siteId === 'all' ? 'opacity-100' : 'opacity-0')} />
                      {text.all}
                    </CommandItem>
                    {siteOptions.map((s) => (
                      <CommandItem key={s.id} value={s.name} onSelect={() => { setSiteId(s.id); setSiteOpen(false); }}>
                        <Check className={cn('mr-2 h-4 w-4', siteId === s.id ? 'opacity-100' : 'opacity-0')} />
                        {s.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs text-muted-foreground">{text.filterType}</label>
          <Select value={cadenceType} onValueChange={setCadenceType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{text.all}</SelectItem>
              <SelectItem value="ONE_OFF">{text.typeOneOff}</SelectItem>
              <SelectItem value="EXACT_MONTHS">{text.typeRecurring}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.sites.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {text.noExtras}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.sites.map((site) => {
            const isCollapsed = isSiteCollapsed(site);
            const pendingCount = site.extras.filter((e) => e.status === 'completed').length;
            return (
              <Card key={site.site_id}>
                <div className="w-full flex items-center justify-between px-4 py-3 border-b hover:bg-muted/30 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleCollapse(site)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    <span className="font-semibold">{site.site_name}</span>
                    <span className="text-xs text-muted-foreground">
                      · {site.totals.count} st · {site.totals.points} p
                      {isAdmin && ` · ${formatSek(site.totals.price_ex_moms)}`}
                    </span>
                    {pendingCount > 0 && (
                      <Badge variant="warning" className="ml-2">
                        {pendingCount} {language === 'sv' ? 'att godkänna' : 'to approve'}
                      </Badge>
                    )}
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.hash = `/brfs/${site.site_id}?tab=extras`;
                    }}
                    className="ml-2 shrink-0"
                  >
                    {language === 'sv' ? 'Öppna BRF' : 'Open BRF'}
                    <ArrowUpRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>

                {!isCollapsed && (
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                          <th className="px-4 py-2">{text.tblDate}</th>
                          <th className="px-4 py-2">{text.tblLabel}</th>
                          <th className="px-4 py-2">{text.tblCleaner}</th>
                          <th className="px-4 py-2 text-right">{text.tblPoints}</th>
                          {isAdmin && <th className="px-4 py-2 text-right">{text.tblPrice}</th>}
                          <th className="px-4 py-2">{text.tblStatus}</th>
                          <th className="px-4 py-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {site.extras.map((extra) => (
                          <tr key={extra.job_block_id} className="border-b last:border-b-0 hover:bg-muted/30">
                            <td className="px-4 py-3 font-mono text-xs">{extra.scheduled_for}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{extra.label}</div>
                              <div className="text-xs text-muted-foreground">{extra.area || extra.source_code}</div>
                            </td>
                            <td className="px-4 py-3">
                              {extra.cleaner ? extra.cleaner.name : <span className="text-muted-foreground">{text.unassigned}</span>}
                            </td>
                            <td className="px-4 py-3 text-right">{extra.points}</td>
                            {isAdmin && (
                              <td className="px-4 py-3 text-right">{formatSek(extra.price_ex_moms_sek)}</td>
                            )}
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant(extra.status)}>
                                {text[`status${extra.status.charAt(0).toUpperCase() + extra.status.slice(1)}` as keyof typeof text] ?? extra.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {extra.status === 'completed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApprove(extra.job_id)}
                                  disabled={approving === extra.job_id}
                                >
                                  {approving === extra.job_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3" />
                                  )}
                                  <span className="ml-1">{approving === extra.job_id ? text.approving : text.approve}</span>
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
